import { fmtRange, fmtTime, splitLocal } from "./format";

export type AccessEvent = { eventDate: string; eventEndDate?: string | null; startsAt: string | null; endsAt?: string | null; setupStartsAt?: string | null; vacateAt?: string | null; accessFrom?: string | null; accessThrough?: string | null; spanStart?: string; spanEnd?: string; accessStartUtc?: string | null; accessEndUtc?: string | null; unknownBounds?: boolean; reservationMode?: "timed" | "all_day" | "legacy_unresolved" | null; schedule?: unknown };
/** List APIs need only the captured mode, not the private raw migration evidence. */
export function reservationMode(event: AccessEvent): string | null {
  const reservation = typeof event.schedule === "object" && event.schedule !== null && "reservation" in event.schedule ? event.schedule.reservation : null;
  return typeof reservation === "object" && reservation !== null && "mode" in reservation && typeof reservation.mode === "string" ? reservation.mode : event.reservationMode ?? null;
}
/** Date-only phase labels never invent unknown clock times. Operational ranges
 * use occupied access days, while event-directory dates remain event-only. */
export function accessPhase(event: AccessEvent, day: string): string {
  if (event.unknownBounds) return "Protected venue access · Dates unresolved — staff review required";
  const mode = reservationMode(event);
  if (mode === "all_day") return "Explicit full-day venue reservation";
  if (mode === "legacy_unresolved") return "Legacy protected dates · Event and access times need review";
  const eventEnd = event.eventEndDate ?? event.endsAt?.slice(0, 10);
  const start = event.setupStartsAt, end = event.vacateAt;
  const phases: string[] = [];
  if (day < event.eventDate) phases.push("Setup");
  else if (eventEnd && day > eventEnd) phases.push("Cleanup");
  else {
    if (start && event.startsAt && start < event.startsAt && start.slice(0, 10) <= day) phases.push("Setup");
    phases.push("Event");
    if (!eventEnd && day > event.eventDate) phases.push("End date to be confirmed");
    if (end && event.endsAt && end > event.endsAt && end.slice(0, 10) >= day) phases.push("Cleanup");
  }
  const clock = (value: string | null | undefined, fallback: string) => value?.slice(0, 10) === day ? fmtTime(splitLocal(value).time) : fallback;
  const hours = event.accessStartUtc && event.accessEndUtc ? `${clock(start, "Earlier access")}–${clock(end, "Later departure")}` : "Access times to be confirmed";
  return `${phases.join(" · ")} · ${hours}`;
}
export function accessRange(event: AccessEvent): string {
  if (event.unknownBounds) return "Venue access dates unresolved — staff review required";
  const from = event.accessFrom ?? event.spanStart, through = event.accessThrough ?? event.spanEnd;
  return from && through ? `Venue access ${fmtRange(from, through)}` : "Venue access dates to be confirmed";
}
